/**
 * audioConfig.ts — Registro central de todos os assets de audio
 *
 * Para trocar qualquer musica/sfx:
 *   1. Coloque o arquivo .ogg e .mp3 na pasta correspondente em client/public/audio/
 *   2. Ajuste os valores aqui se necessario (volume, fade, etc.)
 *   3. Nenhuma outra mudanca de codigo necessaria
 *
 * Enquanto os arquivos de audio nao existirem, o sistema usa
 * fallback procedural (osciladores Web Audio API) automaticamente.
 */

// ============================================================
// FEATURE FLAG — controla se usa arquivos de audio ou procedural
// Quando true: tenta carregar arquivos, fallback procedural se 404
// Quando false: usa apenas procedural (comportamento anterior)
// ============================================================
export const USE_FILE_AUDIO = true;

// ============================================================
// TIPOS
// ============================================================

export interface MusicTrackConfig {
  /** ID unico usado no codigo */
  id: string;
  /** Paths em ordem de prioridade (primeiro formato suportado vence) */
  src: string[];
  /** Se o track deve fazer loop */
  loop: boolean;
  /** Volume padrao (0-1) relativo ao volume master */
  volume: number;
  /** Duracao do fade in em ms */
  fadeIn: number;
  /** Duracao do fade out em ms */
  fadeOut: number;
  /** Para tracks adaptativos: faixa de tensao/intensidade [min, max] */
  intensityRange?: [number, number];
  /** Estrategia de preload: 'auto' carrega eagerly, 'none' carrega sob demanda */
  preload: 'auto' | 'none';
}

export interface SfxConfig {
  /** ID unico */
  id: string;
  /** Paths em ordem de prioridade */
  src: string[];
  /** Volume padrao (0-1) */
  volume: number;
  /** Quantidade de instancias simultaneas permitidas */
  pool: number;
  /** Variacao de pitch para som organico [min, max] (1.0 = normal) */
  rateRange?: [number, number];
  /** Intervalo minimo entre execucoes (ms) para evitar spam */
  minIntervalMs?: number;
}

export interface StingerConfig {
  /** ID unico */
  id: string;
  /** Paths em ordem de prioridade */
  src: string[];
  /** Volume padrao (0-1) */
  volume: number;
  /** Se true, abaixa volume da musica enquanto toca */
  duckMusic: boolean;
  /** Nivel de duck (0-1, menor = mais duck) */
  duckLevel: number;
}

// ============================================================
// CONTEXTOS DE MUSICA
// ============================================================

export type MusicContext = 
  | 'menu' | 'battle' | 'battle_royale' | 'battle_genai' | 'story' 
  | 'credits' | 'tutorial' | 'game_over' | 'none'
  // Boss themes (story mode)
  | 'boss_rok' | 'boss_nyx' | 'boss_boreal' | 'boss_verdant' 
  | 'boss_titan' | 'boss_corvus' | 'boss_prime';

/** Mapeia contexto para os IDs dos tracks que pertencem a ele */
export const CONTEXT_TRACKS: Record<MusicContext, string[]> = {
  menu: ['menu_main'],
  battle: ['battle_low', 'battle_mid', 'battle_high', 'battle_boss'],
  battle_royale: ['battle_royale'],
  battle_genai: ['battle_genai'],
  story: ['story_calm', 'story_tense', 'chapter1_intro', 'emotional_reveal'],
  credits: ['credits_main', 'credits_extended'],
  tutorial: ['tutorial'],
  game_over: ['game_over'],
  none: [],
  // Boss themes - cada boss tem seu próprio tema único
  boss_rok: ['boss_rok'],
  boss_nyx: ['boss_nyx'],
  boss_boreal: ['boss_boreal'],
  boss_verdant: ['boss_verdant'],
  boss_titan: ['boss_titan'],
  boss_corvus: ['boss_corvus'],
  boss_prime: ['boss_prime'],
};

// ============================================================
// TRACKS DE MUSICA
// ============================================================

export const MUSIC_TRACKS: Record<string, MusicTrackConfig> = {
  // -- MENU --
  menu_main: {
    id: 'menu_main',
    src: ['/audio/music/menu/menu-theme-main.mp3'],
    loop: true,
    volume: 0.5,
    fadeIn: 1500,
    fadeOut: 800,
    preload: 'auto',
  },

  // -- BATTLE (3 tiers adaptativos por tensao) --
  battle_low: {
    id: 'battle_low',
    src: ['/audio/music/battle/battle-low.mp3'],
    loop: true,
    volume: 0.5,
    fadeIn: 500,
    fadeOut: 1000,
    intensityRange: [0.0, 0.39],
    preload: 'auto',
  },
  battle_mid: {
    id: 'battle_mid',
    src: ['/audio/music/battle/battle-mid.mp3'],
    loop: true,
    volume: 0.5,
    fadeIn: 500,
    fadeOut: 1000,
    intensityRange: [0.4, 0.69],
    preload: 'auto',
  },
  battle_high: {
    id: 'battle_high',
    src: ['/audio/music/battle/battle-high.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 500,
    fadeOut: 1000,
    intensityRange: [0.7, 1.0],
    preload: 'auto',
  },
  battle_boss: {
    id: 'battle_boss',
    src: ['/audio/music/battle/battle-boss.mp3'],
    loop: true,
    volume: 0.6,
    fadeIn: 800,
    fadeOut: 1000,
    intensityRange: [0.9, 1.0],
    preload: 'none',
  },
  battle_royale: {
    id: 'battle_royale',
    src: ['/audio/music/battle/battle-royale.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 500,
    fadeOut: 1000,
    preload: 'none',
  },
  battle_genai: {
    id: 'battle_genai',
    src: ['/audio/music/battle/battle-genai.mp3'],
    loop: true,
    volume: 0.5,
    fadeIn: 600,
    fadeOut: 1000,
    preload: 'none',
  },

  // -- STORY (2 tiers adaptativos por intensidade) --
  story_calm: {
    id: 'story_calm',
    src: ['/audio/music/story/story-calm.mp3'],
    loop: true,
    volume: 0.4,
    fadeIn: 2000,
    fadeOut: 1500,
    intensityRange: [0.0, 0.49],
    preload: 'none',
  },
  story_tense: {
    id: 'story_tense',
    src: ['/audio/music/story/story-tense.mp3'],
    loop: true,
    volume: 0.45,
    fadeIn: 800,
    fadeOut: 1500,
    intensityRange: [0.5, 1.0],
    preload: 'none',
  },
  chapter1_intro: {
    id: 'chapter1_intro',
    src: ['/audio/music/story/chapter1-intro.mp3'],
    loop: false,
    volume: 0.5,
    fadeIn: 1000,
    fadeOut: 1000,
    preload: 'none',
  },
  emotional_reveal: {
    id: 'emotional_reveal',
    src: ['/audio/music/story/emotional-reveal.mp3'],
    loop: false,
    volume: 0.55,
    fadeIn: 1500,
    fadeOut: 1500,
    preload: 'none',
  },

  // -- MENU EXTRAS --
  game_over: {
    id: 'game_over',
    src: ['/audio/music/menu/game-over.mp3'],
    loop: false,
    volume: 0.5,
    fadeIn: 500,
    fadeOut: 800,
    preload: 'none',
  },
  tutorial: {
    id: 'tutorial',
    src: ['/audio/music/menu/tutorial.mp3'],
    loop: true,
    volume: 0.4,
    fadeIn: 1000,
    fadeOut: 800,
    preload: 'none',
  },
  credits_extended: {
    id: 'credits_extended',
    src: ['/audio/music/menu/credits-extended.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 1500,
    fadeOut: 1000,
    preload: 'none',
  },

  // -- CREDITS --
  credits_main: {
    id: 'credits_main',
    src: ['/audio/music/menu/credits-extended.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 1000,
    fadeOut: 800,
    preload: 'auto',
  },

  // ==========================================================
  // BOSS THEMES - Temas únicos por boss (Story Mode)
  // Gerar com Suno AI - ver docs/musica/SUNO_BOSS_PROMPTS.md
  // ==========================================================

  // RØK - Cap 2: Industrial metal, força bruta
  boss_rok: {
    id: 'boss_rok',
    src: ['/audio/music/boss/boss-rok.mp3'],
    loop: true,
    volume: 0.6,
    fadeIn: 800,
    fadeOut: 1000,
    preload: 'none',
  },

  // NYX - Cap 3: Dark electronic, stealth assassin
  boss_nyx: {
    id: 'boss_nyx',
    src: ['/audio/music/boss/boss-nyx.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 600,
    fadeOut: 1000,
    preload: 'none',
  },

  // BOREAL - Cap 4: Icy synths, frozen precision
  boss_boreal: {
    id: 'boss_boreal',
    src: ['/audio/music/boss/boss-boreal.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 800,
    fadeOut: 1200,
    preload: 'none',
  },

  // VERDANT - Cap 5: Tribal drums, corrupted nature
  boss_verdant: {
    id: 'boss_verdant',
    src: ['/audio/music/boss/boss-verdant.mp3'],
    loop: true,
    volume: 0.55,
    fadeIn: 1000,
    fadeOut: 1000,
    preload: 'none',
  },

  // TITÃ-AXIS - Cap 6: Epic orchestral + metal, 3 fases
  boss_titan: {
    id: 'boss_titan',
    src: ['/audio/music/boss/boss-titan.mp3'],
    loop: true,
    volume: 0.65,
    fadeIn: 1000,
    fadeOut: 1500,
    preload: 'none',
  },

  // CORVUS-7 - Cap 9: Glitchy cyberpunk, traidor
  boss_corvus: {
    id: 'boss_corvus',
    src: ['/audio/music/boss/boss-corvus.mp3'],
    loop: true,
    volume: 0.6,
    fadeIn: 500,
    fadeOut: 1000,
    preload: 'none',
  },

  // KITSUNE-PRIME - Cap 10: Epic final, emotional
  boss_prime: {
    id: 'boss_prime',
    src: ['/audio/music/boss/boss-prime.mp3'],
    loop: true,
    volume: 0.65,
    fadeIn: 1500,
    fadeOut: 2000,
    preload: 'none',
  },
};

// ============================================================
// STINGERS (one-shots)
// ============================================================

export const STINGERS: Record<string, StingerConfig> = {
  victory_wave: {
    id: 'victory_wave',
    src: ['/audio/stingers/victory-match.mp3'],
    volume: 0.7,
    duckMusic: true,
    duckLevel: 0.3,
  },
  victory_match: {
    id: 'victory_match',
    src: ['/audio/stingers/victory-match.mp3'],
    volume: 0.7,
    duckMusic: true,
    duckLevel: 0.2,
  },
  defeat: {
    id: 'defeat',
    src: ['/audio/stingers/defeat.mp3'],
    volume: 0.6,
    duckMusic: true,
    duckLevel: 0.2,
  },
  boss_warning: {
    id: 'boss_warning',
    src: ['/audio/stingers/defeat.mp3'],
    volume: 0.5,
    duckMusic: false,
    duckLevel: 1.0,
  },
  // Dungeon-specific stingers
  room_clear: {
    id: 'room_clear',
    src: ['/audio/stingers/room-clear.mp3'],
    volume: 0.6,
    duckMusic: true,
    duckLevel: 0.4,
  },
  boss_intro: {
    id: 'boss_intro',
    src: ['/audio/stingers/boss-intro.mp3'],
    volume: 0.65,
    duckMusic: true,
    duckLevel: 0.2,
  },
};

// ============================================================
// SFX
// ============================================================

export const SFX: Record<string, SfxConfig> = {
  // -- Battle --
  shoot: {
    id: 'shoot',
    src: ['/audio/sfx/battle/shoot.mp3'],
    volume: 0.3,
    pool: 5,
    rateRange: [0.95, 1.05],
    minIntervalMs: 30,
  },
  hit: {
    id: 'hit',
    src: ['/audio/sfx/battle/hit.mp3'],
    volume: 0.5,
    pool: 5,
    rateRange: [0.9, 1.1],
    minIntervalMs: 40,
  },
  explosion: {
    id: 'explosion',
    src: ['/audio/sfx/battle/explosion.mp3'],
    volume: 0.6,
    pool: 3,
    rateRange: [0.85, 1.15],
    minIntervalMs: 60,
  },
  charged_shoot: {
    id: 'charged_shoot',
    src: ['/audio/sfx/battle/explosion.mp3'],
    volume: 0.5,
    pool: 2,
  },
  success: {
    id: 'success',
    src: ['/audio/sfx/battle/success.mp3'],
    volume: 0.5,
    pool: 2,
  },
  // -- Terreno (água, lava, gelo, lama) --
  // Sem arquivo publicado ainda: o engine cai no procedural de `terrainSfx`,
  // que é onde estes quatro sons realmente moram. O caminho fica registrado
  // para o dia em que houver gravação — trocar é só publicar o arquivo.
  terrain_splash: {
    id: 'terrain_splash',
    src: ['/audio/sfx/battle/terrain-splash.mp3'],
    volume: 0.4,
    pool: 3,
    rateRange: [0.92, 1.08],
    minIntervalMs: 120,
  },
  terrain_steam: {
    id: 'terrain_steam',
    src: ['/audio/sfx/battle/terrain-steam.mp3'],
    volume: 0.45,
    pool: 2,
    minIntervalMs: 150,
  },
  terrain_mud: {
    id: 'terrain_mud',
    src: ['/audio/sfx/battle/terrain-mud.mp3'],
    volume: 0.4,
    pool: 3,
    rateRange: [0.9, 1.1],
    minIntervalMs: 120,
  },
  terrain_ignite: {
    id: 'terrain_ignite',
    src: ['/audio/sfx/battle/terrain-ignite.mp3'],
    volume: 0.5,
    pool: 2,
    minIntervalMs: 150,
  },
  ui_nav: {
    id: 'ui_nav',
    src: ['/audio/sfx/battle/hit.mp3'],
    volume: 0.18,
    pool: 4,
    rateRange: [0.98, 1.04],
    minIntervalMs: 40,
  },

  // -- Story --
  // NOTA: SFX ambientais usam paths dedicados. Se o arquivo não existir,
  // o fallback procedural (Web Audio) será usado automaticamente.
  // NÃO aponte para arquivos de música — causa sobreposição de áudio.
  ambient_space: {
    id: 'ambient_space',
    src: ['/audio/sfx/story/ambient-space.mp3'],
    volume: 0.15,
    pool: 1,
  },
  ambient_wind: {
    id: 'ambient_wind',
    src: ['/audio/sfx/story/ambient-wind.mp3'],
    volume: 0.12,
    pool: 1,
  },
  ambient_factory: {
    id: 'ambient_factory',
    src: ['/audio/sfx/story/ambient-factory.mp3'],
    volume: 0.12,
    pool: 1,
  },
  ambient_garage: {
    id: 'ambient_garage',
    src: ['/audio/sfx/story/ambient-garage.mp3'],
    volume: 0.1,
    pool: 1,
  },
  suspense: {
    id: 'suspense',
    src: ['/audio/sfx/story/suspense.mp3'],
    volume: 0.2,
    pool: 1,
  },
  power_up: {
    id: 'power_up',
    src: ['/audio/sfx/battle/success.mp3'],
    volume: 0.4,
    pool: 2,
  },
  boot: {
    id: 'boot',
    src: ['/audio/sfx/battle/shoot.mp3'],
    volume: 0.35,
    pool: 1,
  },
  dialogue: {
    id: 'dialogue',
    src: ['/audio/sfx/battle/hit.mp3'],
    volume: 0.15,
    pool: 2,
  },
  alert: {
    id: 'alert',
    src: ['/audio/sfx/battle/explosion.mp3'],
    volume: 0.45,
    pool: 1,
  },
  story_victory: {
    id: 'story_victory',
    src: ['/audio/stingers/victory-match.mp3'],
    volume: 0.5,
    pool: 1,
  },
  credits_music: {
    id: 'credits_music',
    src: ['/audio/sfx/story/credits-music.mp3'],
    volume: 0.55,
    pool: 1,
  },

  // -- Dungeon-specific SFX --
  door_unlock: {
    id: 'door_unlock',
    src: ['/audio/sfx/dungeon/door-unlock.mp3'],
    volume: 0.5,
    pool: 2,
  },
  door_open: {
    id: 'door_open',
    src: ['/audio/sfx/dungeon/door-open.mp3'],
    volume: 0.45,
    pool: 1,
  },
  room_enter: {
    id: 'room_enter',
    src: ['/audio/sfx/dungeon/room-enter.mp3'],
    volume: 0.4,
    pool: 1,
  },
  room_clear_sfx: {
    id: 'room_clear_sfx',
    src: ['/audio/sfx/dungeon/room-clear.mp3'],
    volume: 0.5,
    pool: 1,
  },
  energy_absorb: {
    id: 'energy_absorb',
    src: ['/audio/sfx/dungeon/energy-absorb.mp3'],
    volume: 0.3,
    pool: 3,
    rateRange: [0.9, 1.1],
    minIntervalMs: 200,
  },
  enemy_spawn: {
    id: 'enemy_spawn',
    src: ['/audio/sfx/dungeon/enemy-spawn.mp3'],
    volume: 0.35,
    pool: 2,
    minIntervalMs: 300,
  },
  wave_clear: {
    id: 'wave_clear',
    src: ['/audio/sfx/dungeon/wave-clear.mp3'],
    volume: 0.45,
    pool: 1,
  },
  dungeon_intro: {
    id: 'dungeon_intro',
    src: ['/audio/sfx/dungeon/dungeon-intro.mp3'],
    volume: 0.5,
    pool: 1,
  },
  transition_whoosh: {
    id: 'transition_whoosh',
    src: ['/audio/sfx/dungeon/transition-whoosh.mp3'],
    volume: 0.4,
    pool: 1,
  },
};
